import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Search, Filter, ShoppingBag, ArrowUpRight, CheckCircle, XCircle,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { vendorApi } from '@/services/api';
import { formatNPR } from '@/utils/format';
import { Badge, Button, Card, CardContent, Input, Skeleton } from '@nepal-ecom/ui';

type OrderStatus =
  | 'pending' | 'payment_pending' | 'payment_confirmed' | 'processing'
  | 'packed' | 'shipped' | 'out_for_delivery' | 'delivered'
  | 'cancelled' | 'return_requested' | 'return_approved' | 'returned' | 'refunded';

interface VendorOrderRow {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  paymentMethod: 'esewa' | 'khalti' | 'cod' | 'wallet' | 'bank_transfer';
  isPaid: boolean;
  createdAt: string;
  vendorSubtotal: number;
  shippingAddress?: {
    fullName?: string;
    phone?: string;
    province?: string;
    district?: string;
  } | null;
  items: Array<{ id: string; productName: string; quantity: number }>;
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'Pending',
  payment_pending: 'Payment pending',
  payment_confirmed: 'Payment confirmed',
  processing: 'Processing',
  packed: 'Packed',
  shipped: 'Shipped',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  return_requested: 'Return requested',
  return_approved: 'Return approved',
  returned: 'Returned',
  refunded: 'Refunded',
};

const STATUS_VARIANT: Record<OrderStatus, 'success' | 'warning' | 'destructive' | 'info' | 'secondary'> = {
  pending: 'warning',
  payment_pending: 'warning',
  payment_confirmed: 'info',
  processing: 'info',
  packed: 'info',
  shipped: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  cancelled: 'destructive',
  return_requested: 'warning',
  return_approved: 'warning',
  returned: 'destructive',
  refunded: 'destructive',
};

const PAYMENT_LABEL: Record<string, string> = {
  esewa: 'eSewa',
  khalti: 'Khalti',
  cod: 'COD',
  wallet: 'Wallet',
  bank_transfer: 'Bank / QR',
};

// Backend-driven workflow: vendor can move the order one step forward.
const NEXT_STATUS: Partial<Record<OrderStatus, { status: OrderStatus; label: string }>> = {
  payment_confirmed: { status: 'processing', label: 'Start processing' },
  processing: { status: 'packed', label: 'Mark packed' },
  packed: { status: 'shipped', label: 'Mark shipped' },
  shipped: { status: 'out_for_delivery', label: 'Out for delivery' },
  out_for_delivery: { status: 'delivered', label: 'Mark delivered' },
};

const ALL_STATUSES: OrderStatus[] = Object.keys(STATUS_LABEL) as OrderStatus[];

export function OrderList() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['vendor-orders', statusFilter],
    queryFn: () =>
      vendorApi.getOrders({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 100,
      }),
    select: (res) => res.data.data?.data as VendorOrderRow[] | undefined,
  });

  const advanceMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      vendorApi.updateOrderStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-orders'] });
      toast.success('Order updated');
    },
    onError: (e: any) =>
      toast.error('Update failed', { description: e?.response?.data?.message ?? String(e) }),
  });

  const orders = data ?? [];

  const filtered = orders.filter((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.orderNumber.toLowerCase().includes(q) ||
      (o.shippingAddress?.fullName ?? '').toLowerCase().includes(q)
    );
  });

  const counts = {
    total: orders.length,
    pending: orders.filter((o) => o.status === 'pending' || o.status === 'payment_pending').length,
    processing: orders.filter((o) =>
      ['payment_confirmed', 'processing', 'packed', 'shipped', 'out_for_delivery'].includes(o.status),
    ).length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Orders"
        description={
          isLoading
            ? 'Loading orders…'
            : `${counts.total} total · ${counts.pending} pending · ${counts.processing} in progress · ${counts.delivered} delivered`
        }
      />

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order # or customer…" className="pl-9" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            >
              <option value="all">All status</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Order', 'Customer', 'Items', 'Vendor total', 'Payment', 'Status', 'Date', 'Action'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-4 py-4"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8}>
                  <EmptyState
                    icon={ShoppingBag}
                    title={orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}
                    description={orders.length === 0
                      ? 'When customers buy your products, their orders appear here in real time.'
                      : 'Try clearing the search or status filter.'}
                  />
                </td></tr>
              )}
              {!isLoading && filtered.map((o) => {
                const action = NEXT_STATUS[o.status];
                return (
                  <tr key={o.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <Link to={`/orders/${o.id}`} className="text-sm font-semibold text-slate-900 hover:text-primary-700 tabular-nums">#{o.orderNumber}</Link>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-slate-700">{o.shippingAddress?.fullName ?? '—'}</p>
                      <p className="text-xs text-slate-500">
                        {[o.shippingAddress?.district, o.shippingAddress?.province].filter(Boolean).join(', ') || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-700 tabular-nums">{o.items.length}</td>
                    <td className="px-4 py-3.5 text-sm font-bold text-slate-900 tabular-nums">{formatNPR(o.vendorSubtotal)}</td>
                    <td className="px-4 py-3.5 text-xs">
                      <span className="font-semibold text-slate-700">{PAYMENT_LABEL[o.paymentMethod] ?? o.paymentMethod}</span>
                      {!o.isPaid && <span className="ml-1 text-amber-600">· unpaid</span>}
                    </td>
                    <td className="px-4 py-3.5"><Badge variant={STATUS_VARIANT[o.status]}>{STATUS_LABEL[o.status]}</Badge></td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">{new Date(o.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1">
                        {action && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={advanceMutation.isPending}
                            onClick={() => advanceMutation.mutate({ id: o.id, status: action.status })}
                          >
                            <CheckCircle className="h-3.5 w-3.5" /> {action.label}
                          </Button>
                        )}
                        <Link to={`/orders/${o.id}`} className="p-1.5 rounded-md text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors" title="View">
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
